/**
 * GWALL Picture Frame: Organization Profile Form
 * Frame: T-ORG-PROFILE | Version: v1-simple (web)
 * Category: data | Expected Shape: object
 *
 * Registered in: GWALL.Picture_Frame_Registry (Frame_ID=26) +
 *                GWALL.Picture_Frame_Versions (Version_Code='v1-simple')
 *
 * Groups fields into rows via Component_Field_Definitions.Row_Group,
 * sizing each field within a row via Field_Width (%). This is the
 * composite shape UniversalRenderer dispatches to when
 * component.template === 'T-ORG-PROFILE' (resolved server-side from
 * TGAUI.Component_Registry2.Web_Picture_Frame_Code).
 *
 * Proof-of-concept component: seq 10886 (Org White Label Branding Profile).
 */

import React from 'react';
import { Box, TextField, Switch, FormControlLabel, Button, Typography } from '@mui/material';
import * as Icons from '@mui/icons-material';

interface FrameField {
    name: string;
    type: string;
    label: string;
    width?: number;
    rowGroup?: number;
    required?: boolean;
    defaultValue?: any;
    placeholder?: string;
    helpText?: string;
}

interface FrameButton {
    id: number;
    label: string;
    icon?: string;
    color?: 'primary' | 'success' | 'error' | 'warning' | 'info';
    variant?: 'contained' | 'outlined' | 'text';
}

interface TOrgProfileProps {
    title?: string;
    fields: FrameField[];
    buttons: FrameButton[];
    data?: any;
    loading?: boolean;
    onButtonClick: (button: FrameButton) => void;
}

const renderFieldControl = (field: FrameField, value: any) => {
    switch (field.type) {
        case 'boolean':
            return (
                <FormControlLabel
                    control={<Switch checked={value === 1 || value === true} disabled />}
                    label={field.label}
                />
            );
        case 'text':
        case 'number':
        case 'email':
        case 'date':
            return (
                <TextField
                    label={field.label}
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={value || ''}
                    fullWidth
                    required={field.required}
                    placeholder={field.placeholder}
                    helperText={field.helpText}
                    disabled
                />
            );
        default:
            return (
                <Typography>
                    {field.label}: {String(value)}
                </Typography>
            );
    }
};

export const TORGPROFILE_v1_simple: React.FC<TOrgProfileProps> = ({
    title,
    fields,
    buttons,
    data,
    loading,
    onButtonClick,
}) => {
    // Group fields by Row_Group (ungrouped fields each get their own row)
    const rows = new Map<string, FrameField[]>();
    let ungroupedIndex = 0;
    for (const field of fields || []) {
        const key =
            field.rowGroup !== undefined && field.rowGroup !== null
                ? `g${field.rowGroup}`
                : `u${ungroupedIndex++}`;
        if (!rows.has(key)) rows.set(key, []);
        rows.get(key)!.push(field);
    }

    return (
        <Box>
            {title && (
                <Typography variant="h5" sx={{ mb: 3 }}>
                    {title}
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                {Array.from(rows.values()).map((rowFields, rowIdx) => (
                    <Box key={rowIdx} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {rowFields.map((field) => (
                            <Box
                                key={field.name}
                                sx={{
                                    flexBasis: `${field.width || 100 / rowFields.length}%`,
                                    flexGrow: 1,
                                }}
                            >
                                {renderFieldControl(field, data?.[field.name])}
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(buttons || []).map((button) => {
                    const IconComponent = button.icon ? (Icons as any)[button.icon] : null;
                    return (
                        <Button
                            key={button.id}
                            variant={button.variant || 'contained'}
                            color={button.color || 'primary'}
                            onClick={() => onButtonClick(button)}
                            startIcon={IconComponent ? <IconComponent /> : null}
                            disabled={loading}
                        >
                            {button.label}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TORGPROFILE_v1_simple;
